// Every item the world can hand out.
//
// Moved here from `game/src/inventory.ts` (2026-08-13) so that the simulation
// can answer one mechanical question it could not: **is this key consumed when
// it is used?** That fact is `consumedOnUseText`'s presence, and it gated the
// last piece of the interaction gap -- using a key on a locked door, which is
// room1's whole puzzle and which no recording or search could express while the
// answer lived above the sim.
//
// **Prose belongs here, and that is not a compromise.** An item's name and
// description are world content in exactly the sense `ObjectDef.name` and
// `interact.text` already are, and those have lived in `sim/types.ts` and the
// room JSON since Phase 2. An item is a thing in the world; the catalogue of
// them belongs with the catalogue of everything else in the world.
//
// `game/src/inventory.ts` re-exports all of this, so no call site moved -- the
// thin same-named delegator pattern `14_Refactoring_Method.md` describes. The
// block below is character-for-character what was there.
import { RUNE_SYMBOL } from '../runes/symbols';

export interface ItemDef {
  id: string;
  name: string;
  description: string;
  /** If set, this item is consumed (removed from inventory) the moment it's
   * used to unlock its door (see ui.ts's door-panel key handler), and this
   * text is appended to the unlock narration -- a one-use key, as opposed to
   * an ordinary key/seal that's kept afterward. */
  consumedOnUseText?: string;
}

export const ITEMS: Record<string, ItemDef> = {
  'rusty-key': {
    id: 'rusty-key',
    name: 'Rusty Key',
    description: 'An old iron key, pitted with rust. Might still turn in the right lock.',
    consumedOnUseText: 'The rusty key crumbles to flakes of rust in the lock, spent for good.',
  },
  'unmarked-shard': {
    id: 'unmarked-shard',
    name: 'Unmarked Shard',
    description:
      "Cold, dark, and faintly humming. It isn't Mass, Space, Time, Charge, Entropy, Energy, or Momentum -- or not in any combination you recognize. You have no idea what this does.",
  },
  'archive-key': {
    id: 'archive-key',
    name: 'Cindered Key',
    description: "Pulled from a chest that had no lock to begin with -- entropy took it apart, not a thief. Still faintly warm, like whatever it fits hasn't cooled all the way either.",
  },
  'mass-crystal': {
    id: 'mass-crystal',
    name: `${RUNE_SYMBOL.mass} Crystal`,
    description: "Far heavier than its size lets on -- carrying it feels like it's fighting to stay put in your hand.",
  },
  'entropy-crystal': {
    id: 'entropy-crystal',
    name: `${RUNE_SYMBOL.entropy} Crystal`,
    description: "Laced with hairline fractures that never quite finish spreading. Whatever's inside is still coming apart, slowly.",
  },
  'charge-crystal': {
    id: 'charge-crystal',
    name: `${RUNE_SYMBOL.charge} Crystal`,
    description: "The air around it still prickles faintly, hair-raising, like the instant before a spark jumps.",
  },
  'energy-crystal': {
    id: 'energy-crystal',
    name: `${RUNE_SYMBOL.energy} Crystal`,
    description: "Holds a banked ember's worth of heat without ever quite catching. Warmth rolls off it in slow, steady waves.",
  },
  'warded-key': {
    id: 'warded-key',
    name: 'Warded Key',
    description: 'A plain iron key, kept sealed behind a ward rather than locked in anything itself. Whoever hid it here trusted the archway more than the lock.',
  },
  'proving-seal': {
    id: 'proving-seal',
    name: 'Proving Seal',
    description:
      "A plain iron seal, pulled out of a chest that entropy took apart rather than a lock. It belongs to the Proving Floor and to nothing on the way there -- which is the point: a key that opens a room built for testing must never be one the world outside it can spend.",
  },
  'workshop-key': {
    id: 'workshop-key',
    name: 'Workshop Key',
    description: 'A stout brass key, worn smooth at the grip. Kept behind a gate a machinist trusted more than any lock.',
  },
};

/** Whether using this key on a door consumes it.
 *
 * The one mechanical fact in the catalogue, and the reason it is down here. A
 * one-use key (`consumedOnUseText`) crumbles in the lock; an ordinary key or
 * seal is kept. Read as a predicate rather than as `!!def.consumedOnUseText` at
 * each call site so that "what makes a key one-use" is one decision in one
 * place -- the same call `census.ts`'s `singleUserMechanisms` makes about what
 * counts as a warning. */
export function isConsumedOnUse(itemId: string): boolean {
  return ITEMS[itemId]?.consumedOnUseText !== undefined;
}

export function itemDef(itemId: string): ItemDef | undefined {
  return ITEMS[itemId];
}
